/**
 * LoginPage — simple password gate for team access.
 *
 * The password is set at build time via the VITE_APP_PASSWORD env var.
 * Auth state is stored in sessionStorage so it persists across page
 * navigations within the same tab but is cleared when the tab closes.
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/shared/LanguageSwitcher";
import { ThemeToggle } from "../components/shared/ThemeToggle";

export const AUTH_SESSION_KEY = "auth_ok";
const CONFIGURED_PASSWORD = import.meta.env.VITE_APP_PASSWORD as string | undefined;

export function isAuthenticated(): boolean {
  // If no password is configured, always allow access (dev / unconfigured)
  if (!CONFIGURED_PASSWORD) return true;
  return sessionStorage.getItem(AUTH_SESSION_KEY) === "1";
}

interface LoginPageProps {
  onSuccess: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === CONFIGURED_PASSWORD) {
      sessionStorage.setItem(AUTH_SESSION_KEY, "1");
      setError(false);
      onSuccess();
    } else {
      setError(true);
      setPassword("");
    }
  };

  return (
    <main className="login-page">
      <div className="login-page__topbar">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <div className="login-card">
        <h1 className="login-card__title">{t("login.title")}</h1>
        <p className="login-card__subtitle">{t("login.subtitle")}</p>

        <form className="login-card__form" onSubmit={handleSubmit} noValidate>
          <label className="login-card__label" htmlFor="password">
            {t("login.passwordLabel")}
          </label>
          <input
            id="password"
            className={`login-card__input${error ? " login-card__input--error" : ""}`}
            type="password"
            value={password}
            autoComplete="current-password"
            autoFocus
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(false);
            }}
          />
          {error && (
            <p className="login-card__error" role="alert">
              {t("login.error")}
            </p>
          )}
          <button
            className="btn btn--primary login-card__submit"
            type="submit"
            disabled={!password}
          >
            {t("login.submit")}
          </button>
        </form>
      </div>
    </main>
  );
};
